/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, {
  IresponseData,
  IresponseDataPaginated,
  ImportResult,
  fetchExportBlob,
} from "@/lib/api";
import { EownerType, Subscription } from "./subscription";
import { Client } from "./client";
import { StreamingService } from "./streaming-service";
import { EPaymentMethod, EPaymentStatus } from "./payment";

// Enums
export enum EStreamingAccountStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export enum EStreamingAccountType {
  SHARED = "SHARED",
  DEDICATED = "DEDICATED",
}

export enum ESlotStatus {
  AVAILABLE = "AVAILABLE",
  OCCUPIED = "OCCUPIED",
}

// Interfaces
export interface Slot {
  id: number;
  name: string;
  status: ESlotStatus;
  clientId?: number;
  client?: Client;
  subscriptionItems?: {
    id: number;
    slotId: number;
    streamingAccountId: number;
    subscriptionId: number;
  }[];
  subscriptionEndDate?: string;
  price?: number;
  pin?: string
}

export interface SupplierInfo {
  id: number;
  name: string;
  phone?: string | null;
  email?: string | null;
}

export interface StreamingAccount {
  id: number;
  accountCode: string;
  serviceId: number;
  serviceIcon?: string;
  serviceColor?: string;
  email: string;
  password?: string;
  status: EStreamingAccountStatus;
  type: EStreamingAccountType;
  ownerId?: number;
  ownerName?: string;
  supplierId?: number;
  supplier?: SupplierInfo;
  totalSlots: number;
  slots: Slot[];
  // subscriptions: Subscription[];
  createdAt: string;
  updatedAt: string;
  service: StreamingService;
  subscriptions: Subscription[];
}

// Request DTOs
export interface CreateSlotRequest {
  name: string;
  status?: ESlotStatus;
  clientId?: string;
  clientName?: string;
  subscriptionEndDate?: string;
  price?: number;
  pin?: string;
}

export interface CreateStreamingAccountRequest {
  serviceId: number;
  email: string;
  password?: string;
  status?: EStreamingAccountStatus;
  type: EStreamingAccountType;
  ownerId?: string;
  ownerName?: string;
  supplierId: number;
  pricePlanId: number;
  totalSlots: number;
  slots?: CreateSlotRequest[];
  // Datos de la suscripción del proveedor creada junto con la cuenta
  startDate: string;
  paymentMethod: EPaymentMethod;
  paymentStatus: EPaymentStatus;
}

export interface UpdateSlotRequest {
  name?: string;
  status?: ESlotStatus;
  clientId?: string;
  clientName?: string;
  subscriptionEndDate?: string;
  price?: number;
  pin?: string;
}

export interface UpdateStreamingAccountRequest {
  serviceId?: string;
  email?: string;
  password?: string;
  status?: EStreamingAccountStatus;
  type?: EStreamingAccountType;
  // ownerId?: string;
  // ownerName?: string;
  totalSlots?: number;
  slots?: UpdateSlotRequest[];
  supplierId?: number;
  
  isNotify: boolean
}

// Filter params
export interface StreamingAccountFilters {
  supplierId?: number;
  accountCode?: string;
  serviceId?: number;
  email?: string;
  status?: EStreamingAccountStatus;
  type?: EStreamingAccountType;
  ownerId?: string;
  page?: number;
  limit?: number;
}

// Export / Import
export interface StreamingAccountExportFilters {
  status?: EStreamingAccountStatus;
  serviceId?: number;
  supplierId?: number;
}

export interface ImportStreamingAccountsRequest {
  file: string; // Excel en base64
  updated: boolean;
}

/**
 * Elimina campos vacíos de un objeto
 */
function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

/**
 * Servicio para gestionar cuentas de streaming
 */
export const streamingAccountService = {
  /**
   * Registrar una nueva cuenta de streaming
   */
  async register(
    data: CreateStreamingAccountRequest,
  ): Promise<IresponseData<StreamingAccount>> {
    try {
      const cleanData = removeEmptyFields(data);
      cleanData.supplierId = Number(cleanData.supplierId);
      const response = await apiClient.post<IresponseData<StreamingAccount>>(
        "/streaming-accounts",
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar la cuenta de streaming. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener todas las cuentas de streaming
   */
  async getAll(
    filters?: StreamingAccountFilters,
  ): Promise<IresponseDataPaginated<StreamingAccount[]>> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<
        IresponseDataPaginated<StreamingAccount[]>
      >("/streaming-accounts", { params: cleanFilters });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener las cuentas de streaming. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener una cuenta de streaming por ID
   */
  async getOne(id: string): Promise<IresponseData<StreamingAccount>> {
    try {
      const response = await apiClient.get<IresponseData<StreamingAccount>>(
        `/streaming-accounts/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener la cuenta de streaming. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Actualizar una cuenta de streaming
   */
  async update(
    id: string,
    data: UpdateStreamingAccountRequest,
  ): Promise<IresponseData<StreamingAccount>> {
    try {
      const cleanData = removeEmptyFields(data);
      cleanData.supplierId = Number(cleanData.supplierId);
      const response = await apiClient.put<IresponseData<StreamingAccount>>(
        `/streaming-accounts/${id}`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar la cuenta de streaming. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Registrar un nuevo slot en una cuenta de streaming
   */
  async registerSlot(
    accountId: string | number,
    data: { name: string; pin?: string },
  ): Promise<IresponseData<Slot>> {
    try {
      const response = await apiClient.post<IresponseData<Slot>>(
        `/streaming-accounts/${accountId}/slot/register`,
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar el slot. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Actualizar un slot en una cuenta de streaming
   */
  async updateSlot(
    slotId: string | number,
    data: { name?: string; pin?: string },
  ): Promise<IresponseData<Slot>> {
    try {
      const response = await apiClient.put<IresponseData<Slot>>(
        `/streaming-accounts/slot/${slotId}/update`,
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar el slot. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Exportar cuentas de streaming a un archivo (Excel)
   */
  async exportAccounts(
    filters?: StreamingAccountExportFilters,
  ): Promise<{ blob: Blob; fileName: string }> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      return await fetchExportBlob(
        "/streaming-accounts/export",
        cleanFilters,
        `cuentas-export-${Date.now()}.xlsx`,
      );
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al exportar las cuentas de streaming. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Importar cuentas de streaming desde un archivo Excel (base64)
   */
  async importAccounts(
    data: ImportStreamingAccountsRequest,
  ): Promise<IresponseData<ImportResult>> {
    try {
      const response = await apiClient.post<IresponseData<ImportResult>>(
        "/streaming-accounts/import",
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al importar las cuentas de streaming. Por favor, intenta de nuevo.",
      );
    }
  },
};
